import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';
type ButtonCorners = 'asymmetric' | 'asymmetric-inverse' | 'rounded' | 'sharp';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  corners?: ButtonCorners;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: `
    bg-primary text-text-inverse font-semibold
    hover:bg-primary-hover hover:shadow-glow
    active:bg-primary-dark
    transition-all duration-300
  `,
  secondary: `
    bg-surface border border-border text-text-primary
    hover:bg-surface-hover hover:border-primary hover:shadow-glow
    transition-all duration-300
  `,
  ghost: `
    bg-transparent text-text-secondary
    hover:bg-surface-hover hover:text-text-primary
    transition-colors duration-300
  `,
  danger: `
    bg-transparent border border-primary text-primary font-semibold
    hover:bg-primary hover:text-text-inverse hover:shadow-glow
    transition-all duration-300
  `,
  outline: `
    bg-transparent border border-primary text-primary
    hover:bg-primary hover:text-text-inverse hover:shadow-glow
    transition-all duration-300
  `,
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-5 py-2.5 text-base gap-2',
  lg: 'px-7 py-3.5 text-lg gap-2.5',
  icon: 'p-2.5',
};

const cornerStyles: Record<ButtonCorners, string> = {
  'asymmetric': 'rounded-asymmetric-md',
  'asymmetric-inverse': 'rounded-asymmetric-inverse-md',
  'rounded': 'rounded-lg',
  'sharp': 'rounded-none',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      corners = 'asymmetric',
      loading = false,
      icon,
      iconPosition = 'left',
      className = '',
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        className={`
          inline-flex items-center justify-center font-mono tracking-tight
          focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background
          disabled:pointer-events-none disabled:opacity-40
          ${cornerStyles[corners]}
          ${variantStyles[variant]}
          ${sizeStyles[size]}
          ${className}
        `}
        disabled={isDisabled}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {!loading && icon && iconPosition === 'left' && icon}
        {children}
        {!loading && icon && iconPosition === 'right' && icon}
      </button>
    );
  }
);

Button.displayName = 'Button';
